import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { CONFIG } from 'app/config/config';
import { ERoleType, IUser } from 'app/core/models/model.model';
import { UserService } from 'app/core/services/user/user.service';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, Subject, throwError, TimeoutError } from 'rxjs';
import {
  EAuthState,
  IJwtLoginResponse,
  IJwtToken,
  IRegisterUserPayload,
} from './auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #httpClient = inject(HttpClient);
  #router = inject(Router);
  #userService = inject(UserService);
  #authTokenMetaData: IJwtToken | undefined;
  loginErrorMessage = signal<string | null>(null);
  registrationErrorMessage = signal<string | null>(null);
  authState$ = new Subject<EAuthState>();

  get authTokenMetaData(): IJwtToken | undefined {
    if (this.#authTokenMetaData) {
      return this.#authTokenMetaData;
    }
    const metaData = localStorage.getItem(
      `${environment.appName}-TokenMetadata`
    );
    if (metaData) {
      this.#authTokenMetaData = JSON.parse(metaData);
      return this.#authTokenMetaData;
    }
    return undefined;
  }

  set authTokenMetaData(authTokenMetaData: IJwtToken | undefined) {
    if (!authTokenMetaData) {
      //  clearTimeout(this.refreshTokenHandler);
      localStorage.removeItem(`${environment.appName}-TokenMetadata`);
      this.#authTokenMetaData = undefined;
    } else {
      this.#authTokenMetaData = authTokenMetaData;
      localStorage.setItem(
        `${environment.appName}-TokenMetadata`,
        JSON.stringify(authTokenMetaData)
      );
      // this.activateRefreshToken();
    }
  }

  handleAuthentication({
    identifier,
    password,
  }: {
    identifier: string;
    password: string;
  }) {
    this.clearMessages();
    this.authState$.next(EAuthState.LOGIN_IN_PROGRESS);
    this.#httpClient
      .post<IJwtLoginResponse>(`${environment.apiUrl}/auth/local`, {
        identifier,
        password,
      })
      .subscribe({
        next: (response) => {
          this.signIn(response);
        },
        error: (response) => {
          this.onLoginError(this.getLoginErrorMessage(response));
        },
      });
  }

  registerUser({ email, password }: IRegisterUserPayload) {
    this.clearMessages();
    this.authState$.next(EAuthState.LOGIN_IN_PROGRESS);

    return this.#httpClient
      .post(`${environment.apiUrl}/auth/register-with-role`, {
        email,
        password,
        username: email,
        role: ERoleType.AGENT,
        signUpOrigin: 'ai',
      })
      .pipe(
        map(() => {
          this.authState$.next(EAuthState.TO_BE_LOGGED);
          return true;
        }),
        catchError((response) => {
          this.onRegistrationError(this.getRegistrationErrorMessage(response));
          return throwError(() => response);
        })
      );
  }

  handleRedirect() {
    this.#router.navigate([CONFIG.urls.HOME]);
  }

  signIn(loginResponse?: IJwtLoginResponse) {
    this.authTokenMetaData = this.decodeToken(loginResponse?.jwt);
    this.authState$.next(EAuthState.TOKEN_RETRIEVED);
  }

  decodeToken(token?: string): IJwtToken | undefined {
    if (!token) {
      return undefined;
    }

    const decodedToken: any = jwtDecode(token);
    Object.assign(decodedToken, {
      token: token,
      expiresAt: decodedToken.exp * 1000,
      iat: decodedToken.iat * 1000,
      exp: decodedToken.exp * 1000,
    });

    return decodedToken as IJwtToken;
  }

  isTokenValid(): boolean {
    return !this.isTokenExpired();
  }

  async renewToken(): Promise<IJwtToken> {
    this.signIn(undefined);
    return Promise.reject();
  }

  isTokenExpired(): boolean {
    if (this.authTokenMetaData && this.authTokenMetaData.expiresAt) {
      return Date.now() > this.authTokenMetaData.expiresAt;
    }
    return true;
  }

  loginUserWithToken() {
    return this.#httpClient
      .get<IUser>(`${environment.apiUrl}/users/me`, {
        params: {
          'populate[role]': 'true',
        },
      })
      .pipe(
        map((response: IUser) => {
          this.#userService.user = response;
          if (!response) {
            throw new Error('Empty user!');
          }
          this.authState$.next(EAuthState.LOGGED);
          return true;
        }),
        catchError((error) => {
          if (!(error instanceof TimeoutError)) {
            this.signOut();
          }
          throw error;
        })
      );
  }

  signOut() {
    this.clearAuthData();
    this.authState$.next(EAuthState.TO_BE_LOGGED);
    setTimeout(() => {
      location.href = CONFIG.urls.HOME;
    }, 350);
  }

  clearAuthData() {
    this.#userService.user = undefined;
    this.authTokenMetaData = undefined;
  }

  onLoginError(
    errorMessage = '',
    authState: EAuthState = EAuthState.LOGIN_ERROR
  ) {
    this.loginErrorMessage.set(errorMessage);
    this.registrationErrorMessage.set(null);
    this.authState$.next(authState);
    this.clearAuthData();
  }

  onRegistrationError(
    errorMessage = '',
    authState: EAuthState = EAuthState.LOGIN_ERROR
  ) {
    this.registrationErrorMessage.set(errorMessage);
    this.loginErrorMessage.set(null);
    this.authState$.next(authState);
    this.clearAuthData();
  }

  clearMessages() {
    this.loginErrorMessage.set(null);
    this.registrationErrorMessage.set(null);
  }

  private getLoginErrorMessage(response: any) {
    switch (response?.error?.error?.message) {
      case 'Invalid identifier or password':
        return 'Email o password invalidi';
      default:
        return 'Qualcosa è andato storto, riprova più tardi';
    }
  }

  private getRegistrationErrorMessage(response: any) {
    switch (response?.error?.error?.message) {
      case 'Email or Username are already taken':
      case 'Email is already taken':
      case 'Username is already taken':
        return 'Esiste gia un account registrato con questa email';
      default:
        return 'Registrazione non riuscita, riprova piu tardi';
    }
  }
}
